/** Satisfaction survey (D-129): a note from 1 to 5, solved or not, a comment. Used by the
 * learner space and by the public link of the « resolved » e-mail. */

import "./support.css";

import { useSupportPublicCsatCreate, useSupportPublicCsatRetrieve } from "@agencyos/api-client";
import { Button, Checkbox, GlassCard, Skeleton, TextAreaField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export interface RatingValue {
  score: number;
  resolved: boolean;
  comment: string;
}

export function RatingForm({ pending, onSubmit }: { pending: boolean; onSubmit: (value: RatingValue) => void }) {
  const { t } = useTranslation();
  const [score, setScore] = useState(0);
  const [resolved, setResolved] = useState(true);
  const [comment, setComment] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (score) onSubmit({ score, resolved, comment: comment.trim() });
  };
  return (
    <form className="stack" onSubmit={submit} aria-label={t("support.me.rate")}>
      <fieldset className="plain-fieldset stack">
        <legend>{t("support.me.rate")}</legend>
        <p className="muted">{t("support.me.rateHint")}</p>
        <div className="support-stars">
          {[1, 2, 3, 4, 5].map((value) => (
            <Button key={value} type="button" variant={score === value ? "primary" : "secondary"} aria-pressed={score === value} aria-label={t("support.me.star", { count: value })} onClick={() => setScore(value)}>
              {value}
            </Button>
          ))}
        </div>
      </fieldset>
      <Checkbox label={resolved ? t("support.me.solved") : t("support.me.notSolved")} checked={resolved} onChange={(e) => setResolved(e.target.checked)} />
      <TextAreaField label={t("support.me.comment")} value={comment} rows={3} maxLength={2000} onChange={(e) => setComment(e.target.value)} />
      <Button type="submit" disabled={!score || pending}>
        {t("support.me.send")}
      </Button>
    </form>
  );
}

/** /support/csat/:token — the link of the e-mail, no account needed. */
export function CsatPublicPage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const state = useSupportPublicCsatRetrieve(token, { query: { retry: false } });
  const send = useSupportPublicCsatCreate({ mutation: { onSuccess: () => void state.refetch() } });
  const data = state.data?.status === 200 ? state.data.data : undefined;
  let content;
  if (state.isPending) content = <Skeleton style={{ height: 160 }} />;
  else if (!data) content = <p role="alert">{t("support.csatPage.notFound")}</p>;
  else if (data.answered)
    content = (
      <>
        <p role="status">{t("support.csatPage.already", { score: data.score ?? "" })}</p>
        {data.status === "open" ? <p>{t("support.csatPage.reopened")}</p> : null}
      </>
    );
  else
    content = (
      <>
        <p>{t("support.csatPage.intro", { subject: data.subject })}</p>
        <RatingForm pending={send.isPending} onSubmit={(value) => send.mutate({ token, data: value })} />
      </>
    );
  return (
    <main className="app-main support-public">
      <GlassCard as="section" className="stack" aria-labelledby="csat-title">
        <h1 id="csat-title">{t("support.csatPage.title", { number: data?.number ?? "" })}</h1>
        {content}
      </GlassCard>
    </main>
  );
}
